import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

const fmt = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

function useCountUp(target, durationMs = 1400) {
  const [val, setVal] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const animate = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    startedRef.current = true;
    return () => raf && cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return val;
}

function Bar({ raised, goal, color }) {
  const pct = Math.min(100, (raised / Math.max(1, goal)) * 100);
  return (
    <div className="mt-4">
      <div className="h-[3px] w-full bg-white/10 overflow-hidden">
        <div
          className="h-full transition-all duration-1000 ease-out"
          style={{
            width: `${pct}%`,
            background: color,
            boxShadow: `0 0 14px ${color}`,
          }}
        />
      </div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">
        {pct.toFixed(0)}% funded
      </p>
    </div>
  );
}

function Metric({ label, goal, raised, color, testId }) {
  const animated = useCountUp(raised);
  return (
    <div data-testid={testId} className="glass-panel p-7 md:p-8 relative overflow-hidden group hover:border-white/20 transition-all">
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-20 blur-3xl group-hover:opacity-40 transition-opacity"
        style={{ background: color }}
      />
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-4xl md:text-5xl font-light text-white mt-3">
        {fmt(animated)}
      </p>
      <p className="text-xs text-white/40 mt-1 font-mono">
        of {fmt(goal)} goal
      </p>
      <Bar raised={raised} goal={goal} color={color} />
    </div>
  );
}

export default function ImpactDashboard() {
  const [impact, setImpact] = useState(null);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    api.get("/impact").then((r) => setImpact(r.data)).catch(() => {});
    api.get("/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = impact || {
    sponsorship_goal: 25000, sponsorship_raised: 0,
    scholarship_goal: 10000, scholarship_raised: 0,
    partnership_goal: 50000, partnership_raised: 0,
    total_goal: 85000, total_raised: 0,
  };

  return (
    <section
      id="impact"
      data-testid="impact-dashboard"
      className="relative py-24 md:py-32 px-6 md:px-12 max-w-[1500px] mx-auto scroll-mt-nav"
    >
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-7">
          <p className="text-overline text-aawta-pink mb-4">LIVE FUNDRAISING DASHBOARD</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
            Every dollar fuels<br />
            <span className="text-gradient-pink">workforce impact.</span>
          </h2>
        </div>
        <div className="md:col-span-5 md:text-right">
          <p className="text-overline text-white/40">Total Goal</p>
          <p className="font-display text-5xl md:text-6xl font-light text-white">{fmt(data.total_goal)}</p>
          <p className="text-sm text-white/60 mt-1 font-mono">
            {fmt(data.total_raised)} raised &middot; {(data.total_raised / Math.max(1, data.total_goal) * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Metric testId="impact-sponsorship" label="Sponsorship" goal={data.sponsorship_goal} raised={data.sponsorship_raised} color="#FF00F5" />
        <Metric testId="impact-scholarship" label="Scholarship" goal={data.scholarship_goal} raised={data.scholarship_raised} color="#2F3B99" />
        <Metric testId="impact-partnership" label="Strategic Partnership" goal={data.partnership_goal} raised={data.partnership_raised} color="#FF00F5" />
      </div>

      {metrics && (
        <div data-testid="capacity-impact-strip" className="grid grid-cols-2 md:grid-cols-6 gap-px bg-white/10 border border-white/10 mt-10">
          <Stat label="Tickets Sold" value={metrics.tickets_sold} hint={`${metrics.seats_remaining} seats left`} />
          <Stat label="VIP Tables Left" value={metrics.vip_tables_remaining} hint={`of ${metrics.vip_tables_available}`} />
          <Stat label="Sponsors Secured" value={metrics.sponsors_secured} />
          <Stat label="Scholarships Funded" value={metrics.scholarships_funded} />
          <Stat label="Workforce Served" value={metrics.workforce_participants} />
          <Stat label="Industry Partners" value={metrics.industry_partnerships} />
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="bg-aawta-jet p-4 md:p-5">
      <p className="font-display text-2xl md:text-3xl text-white">{Number(value || 0).toLocaleString()}</p>
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">{label}</p>
      {hint && <p className="text-[10px] text-white/30 mt-1">{hint}</p>}
    </div>
  );
}
